import React from 'react';
import FullWidthBarBreadcrumbs from '@/components/Breadcrumb/FullWidthBarBreadcrumbs';
import CenteredContentSection from '@/components/ContentSection/CenteredContentSection';
import { H3, H4 } from '@/components/Element/Heading';
// import VerticalLeadFormSection from '@/components/LeadForm/VerticalLeadFormSection';
import MetaTags from '@/components/Head/MetaTags';
// import SimpleCenteredHeaderSection from '@/components/HeaderSection/SimpleCenteredHeaderSection';
import Image from 'next/legacy/image';
// import ReadingTime from '@/components/ReadingTime/ReadingTime';

import SideCardCTA from '@/components/SideCardCTA/SideCardCTA2';
import Link from 'next/link';
import ImageOne from 'images/solar-guide/image21.jpg';
import ImageTwo from 'images/solar-guide/image48.png';
import ImageThree from 'images/solar-guide/image59.png';
import ImageFour from 'images/solar-guide/image25.png';
import ImageFive from 'images/solar-guide/image27.jpg';
import FullWidthGuideFormSection from '@/components/LeadForm/FullWidthGuideForm';
import ReadingTime from '@/components/ReadingTime/ReadingTime';
import LinkTable from '@/components/LinkTable/LinkTable';

const links = [
  { href: '/home-solar-guide/why-go-solar', title: '🌞 Why Go Solar?' },
  {
    href: '/home-solar-guide/what-components-make-up-a-solar-system',
    title: '💡 Components of a Solar System',
  },
  {
    href: '/home-solar-guide/solar-panels',
    title: '🌤️ Solar Panel Database',
  },
  {
    href: '/home-solar-guide/battery-storage-products',
    title: '🔋 Battery Storage Products',
  },
  {
    href: '/home-solar-guide/steps-to-install-a-home-solar-system',
    title: '⏭️ Installing a Home Solar System',
  },
  {
    href: '/home-solar-guide/economics-of-home-solar',
    title: '💸 Economics of Home Solar',
  },
  {
    href: '/home-solar-guide/purchase-and-financing-options',
    title: '💳 Purchase & Financing Options',
  },
  {
    href: '/home-solar-guide/what-affects-solar-project-viability',
    title: '✅ Solar Project Viability',
  },
  {
    href: '/home-solar-guide/calculating-solar-production',
    title: '🧮 Calculating Solar Production',
  },
  {
    href: '/home-solar-guide/choosing-the-right-solar-system-for-your-home',
    title: '🔎 Choosing the Right Solar System',
  },
  {
    href: '/home-solar-guide/finding-reputable-solar-and-battery-installers',
    title: '🏠 Finding the Right Installer',
  },
  {
    href: '/home-solar-guide/getting-started',
    title: '🌞 Getting Started With Home Solar',
  },
];
export const SolarGuideTable = () => {
  return (
    <div className="mt-5 mb-[-24px]">
      <LinkTable
        header="Home Solar Guide"
        subheader="Check out key sections from our guide"
        links={links}
      />
    </div>
  );
};
const Page = () => {
  return (
    <article>
      <MetaTags
        title="🌞 Why Go Solar?"
        description="Switching to solar power offers numerous benefits for you and the environment."
      />
      <FullWidthBarBreadcrumbs
        pages={[
          {
            name: 'Home Solar Guide',
            href: '/home-solar-guide',
          },
          {
            name: '🌞 Why Go Solar?',
            href: '/home-solar-guide/why-go-solar',
          },
        ]}
      />
      <CenteredContentSection>
        <div className="flex justify-between items-center">
          <div></div>
          <Link
            href={'/home-solar-guide/what-components-make-up-a-solar-system'}
          >
            What Components Make Up A Solar System? →
          </Link>
        </div>

        <section id="section1">
          <H3>🌞 Why Go Solar?</H3>
          <ReadingTime wordCount={1094} />
          <p>
            <strong>
              Switching to solar power offers numerous benefits for you and the
              environment.
            </strong>{' '}
            By harnessing the sun&apos;s energy to generate electricity, you can
            reduce your reliance on traditional utility sources and take control
            of your energy consumption. Below, we explore the reasons why you
            should consider embracing solar power for your home. From cost
            savings to environmental impact and increased property value.
          </p>
          <Image
            src={ImageOne}
            alt="Electricity, generation,transmission and distribution"
          />
          <H4>🌞 Solar vs. 🏭 Utility</H4>
          <p>
            <strong>
              Throughout North America conventional energy costs are rising due
              to a couple of factors:
            </strong>
          </p>
          <ul>
            <li>
              Generation plants, transmission and distribution systems are
              getting older, and maintenance and replacement costs are
              increasing.
            </li>
            <li>
              Non-renewable fuels for power generation are increasing in price
              from both limited supply and from tighter environmental
              regulations and emission taxes.
            </li>
            <li>
              Electricity demand is increasing with population, larger homes,
              and increasing utilization of air conditioning, electronics and
              electric vehicles.
            </li>
          </ul>
          <p>
            This is where residential solar comes in. Solar is an
            environmentally friendly solution to offset utility costs and help
            off load the reliance on conventional energy solutions.
          </p>
          <Image src={ImageTwo} alt="Bill savings" />
          <H4>💰 Bill Savings</H4>
          <p>
            Did you know? Solar can save you over 30% on your cost of
            electricity. This is the main driver for the adoption of solar -{' '}
            <strong>it saves you money!</strong>{' '}
          </p>
          <p>
            When you think of home solar. The one that makes the most sense.
            It’s the one that produces its own electricity at effective rates
            that are less than you are paying your existing utility.
          </p>
          <p>
            Electricity rates continue to increase over time -{' '}
            <strong>
              since 1970 the price of electricity has increased over 4% per year
              on average.
            </strong>{' '}
            In contrast, energy from a solar system has a fixed price. With
            solar, you won’t have to worry about how the price of your
            electricity changing in the future.
          </p>
          <Image src={ImageThree} alt="Electric price history" />
          <p>
            <Link href="/solar-faq/how-much-can-i-save-with-solar-energy">
              Solar savings
            </Link>{' '}
            <strong>varies on many factors. The most important are:</strong>
          </p>
          <ul>
            <li>Current cost of power from your utility company</li>
            <li>Local net metering rules & regulations</li>
            <li>Sunlight hours on their property</li>
            <li>Shade</li>
            <li>Roof Orientation</li>
            <li>Roof Material</li>
            <li>Installation cost</li>
            <li>Financing charges to install solar.</li>
          </ul>
          <H4>♻️ Sustainability</H4>
          <p>
            By installing solar, you can reduce your own personal{' '}
            <Link href="/solar-faq/how-does-solar-energy-benefit-the-environment">
              environmental footprint!
            </Link>
          </p>
          <p>
            According to the{' '}
            <a
              target="_blank"
              rel="noreferrer"
              href="https://www.epa.gov/ghgemissions/sources-greenhouse-gas-emissions#:~:text=are%20non%2Demitting.-,Emissions%20and%20Trends,percent%20of%20the%20U.S.%20total."
            >
              Environmental Protection Agency (EPA)
            </a>
            , the energy sector accounts for 27 percent of America’s carbon
            emissions. Fossil fuel power plants create carbon emissions which
            contribute to local air pollution in areas close to power plants.
            When you put a solar system on your property, you can avoid
            purchasing hydrocarbon-generated electricity from the grid by
            generating electricity directly with your solar panels instead.{' '}
          </p>
          <p>
            Even when you’re not using the solar electricity directly, you’re
            offsetting the need for hydrocarbon-generated electricity by
            exporting your excess solar energy into the grid. At the end of the
            day,{' '}
            <strong>
              a typical residential solar system will eliminate 3-4 tons of{' '}
              <Link href="/solar-faq/what-is-the-carbon-footprint-of-a-solar-panel-system">
                carbon emissions
              </Link>{' '}
              each year—the equivalent of planting over 100 trees annually.
            </strong>
          </p>
          <p>
            Additionally, by producing energy at the point of consumption, solar
            takes significant stress off the power grid by matching peak load
            demand with local supply. Solar generation is maximal throughout the
            day, which tends to be the period during which homes consume the
            most energy. Solar therefore helps reduce the need for future high
            voltage transmission lines. With local production of power
            communities gain a more versatile and secure energy infrastructure.
          </p>
          <Image src={ImageFour} alt="House in a storm with solar energy." />
          <H4>🛡️ Energy Security</H4>
          <p>
            Home energy security can be achieved by combining solar power with a
            battery system.
          </p>
          <p>
            For safety reasons, solar alone does not function during a power
            outage in the absence of a storage location for excess energy.{' '}
            <strong>
              Pairing solar panels with a battery allows you to recharge with
              sunlight and keep your appliances running even when the grid goes
              down.
            </strong>{' '}
            Batteries are designed to integrate with solar panels and can store
            energy generated during the day for use at night or during
            unexpected outages and grid shut-offs.
          </p>
          <p>
            There is nothing quite like the feeling of being the only house on
            the block with the lights on after the grid goes down—although the
            more altruistic among us would prefer that all our neighbors had the
            same luxury. With a solar battery the transition from grid to
            battery backup power is seamless and reassuring.
          </p>
          <p>
            <strong>
              There are many reasons that you may opt to include batteries in
              your solar project. Some of the most common are:
            </strong>
          </p>
          <ul>
            <li>
              You work from home and need continuous power for
              lights/computers/internet
            </li>
            <li>
              You experience frequent grid failures and lose valuable
              perishables (food)
            </li>
            <li>
              You experience frequent grid failures and lose valuable
              perishables (food)
            </li>
            <li>You have vital medical devices that require electricity</li>
            <li>
              You want to ensure your furnaces can run during winter so their
              pipes don&apos;t freeze
            </li>
            <li>
              You have valuables in your home that require a constant
              temperature
            </li>
            <li>
              You want to ensure your alarm system performs 24/7, 365 days a
              year
            </li>
            <li>
              You want to self-consume as much solar power to be less reliant on
              dirty grid power
            </li>
            <li>
              Your utility company&apos;s solar rate plans aren&apos;t great and
              a battery improves solar ROI
            </li>
            <li>
              You want to position your home as “net zero” and self-reliant to
              increase property value
            </li>
            <li>You just want it!</li>
          </ul>
          <Image src={ImageFive} alt="House with solar panels" />
          <H4>⤴️ Increase Your Home Value </H4>
          <p>
            Many homeowners, buyers, and appraisers know that the installation
            of solar panels and solar batteries can increase a property&apos;s
            market value. In fact, one study from the{' '}
            <a
              target="_blank"
              rel="noreferrer"
              href="https://emp.lbl.gov/news/berkeley-lab-illuminates"
            >
              {' '}
              Lawrence Berkeley National Laboratory
            </a>{' '}
            confirmed that solar installations increase a home&apos;s resale
            value by up to $5,911 for each kilowatt of solar panels installed.
            Another study from{' '}
            <a
              target="_blank"
              rel="noreferrer"
              href="https://www.zillow.com/research/solar-panels-house-sell-more-23798/"
            >
              Zillow showed an increase of 4.1% of the home&apos;s value.
            </a>{' '}
          </p>
          <p>
            <strong>
              More than 80% of homebuyers say that energy efficiency is a top
              priority.
            </strong>
          </p>
          <p>
            Solar panels reduce operating costs and have little to no
            maintenance which allows homebuyers to more easily afford a house
            with solar panels. With increasing grid uncertainty and reliance on
            home energy for remote work solar batteries are becoming a
            requirement for many new homebuyers.
          </p>
          <p>
            Even some states like California have made it{' '}
            <strong>mandatory</strong> for all new constructed residences to
            have solar in order to be livable. As time goes on more and more
            states will follow suit.
          </p>
          <p>
            A unique perk to solar investments, compared to a bathroom
            renovation, is that solar is generally 100% property tax increase
            exempted. Which means while the appraised value of the home may go
            up and the market value of the home will go up from solar, the
            property taxes <strong>will not go up.</strong> This property tax
            exemption is regional and some areas do not have any exemptions.
            Adding solar is an easy - no-demolition needed - home improvement
            that can be cash-flow positive. Try saying that about windows!
          </p>
          <p>
            <strong>
              Sign up below to understand if home solar is right for you:
            </strong>
          </p>
        </section>
      </CenteredContentSection>
      {/* <VerticalLeadFormSection /> */}
      {/* <SideCardContent titles={['The Difficulty of Installation']} /> */}
      <SideCardCTA />
      <FullWidthGuideFormSection cid={'a4oqmxxt5un31ha0'} />
      <SolarGuideTable />
      {/* <CenteredContentSection>
        <div className="flex justify-between items-center">
          <Link href={'/home-solar-guide'}>← Solar Guide</Link>
          <Link
            href={'/home-solar-guide/what-components-make-up-a-solar-system'}
          >
            What Components Make Up A Solar System? →
          </Link>
        </div>
      </CenteredContentSection> */}
      {/* <H4>Now, let&apos;s look into how home solar works:</H4> */}
    </article>
  );
};

export default Page;
